<div class="section-heading">
  <div><span class="section-kicker">Website settings</span><h2>Branding</h2>
    <p>Choose the site logo displayed throughout the website.</p></div>
</div>
<div class="panel branding-panel" [attr.aria-busy]="brandingSaving">
  <div class="logo-preview"><img [src]="branding.logoUrl()" alt="Current site logo" /></div>
  <div class="branding-controls">
    <h3>Site Logo</h3>
    <p>Used throughout the website wherever the franchise logo appears. JPEG, PNG, or WebP, up to 5 MB. A transparent PNG works well on light and dark backgrounds.</p>
    @if (branding.loadFailed()) {
      <p role="status">Unable to load branding settings.</p>
      <button type="button" (click)="branding.load()">Try again</button>
    } @else if (!branding.settings()) {
      <p role="status">Loading branding settings…</p>
    }
    <label for="site-logo-upload">Upload / Replace Logo</label>
    <input id="site-logo-upload" type="file" accept="image/png,image/jpeg,image/webp" (change)="uploadLogo($event)" [disabled]="brandingSaving || !branding.settings()" />
    <button type="button" (click)="resetLogo()" [disabled]="brandingSaving || !branding.settings() || branding.settings()?.usingDefault">Reset to Default</button>
    @if (brandingSaving) { <p role="status">Saving site logo…</p> }
  </div>
</div>
<!-- =========================================================
     WEBSITE SETTINGS
     ========================================================= -->

<div class="section-heading">
  <div>
    <span class="section-kicker">
      Website settings
    </span>

    <h2>
      Contact information
    </h2>

    <p>
      Manage the contact details and email destinations
      used throughout the Holowicki website.
    </p>
  </div>
</div>


<div class="panel settings-panel">

  @if (siteSettingsLoading) {

    <div class="empty-state large">
      <strong>
        Loading website settings...
      </strong>
    </div>

  } @else {

    <form
      (ngSubmit)="saveContactSettings()"
      novalidate
    >

      <!-- =====================================================
           PUBLIC CONTACT INFORMATION
           ===================================================== -->

      <div class="settings-group">

        <div class="settings-group-heading">
          <span class="section-kicker">
            Public information
          </span>

          <h3>
            Website contact details
          </h3>

          <p>
            These values may be displayed publicly
            throughout the website.
          </p>
        </div>


        <div class="form-grid">

          <div class="form-field full-width">
            <label for="contact-email">
              Contact Us email
            </label>

            <input
              id="contact-email"
              name="contactEmail"
              type="email"
              [(ngModel)]="contactSettings.contactEmail"
              placeholder="contact@example.com"
              required
            />

            <small>
              Messages submitted through the Contact Us
              form will be sent to this address.
            </small>
          </div>


          <div class="form-field">
            <label for="contact-phone">
              Public phone number
            </label>

            <input
              id="contact-phone"
              name="contactPhone"
              type="tel"
              [(ngModel)]="contactSettings.contactPhone"
              placeholder="(937) 555-1234"
            />
          </div>


          <div class="form-field">
            <label for="contact-address">
              Business address
            </label>

            <input
              id="contact-address"
              name="contactAddress"
              [(ngModel)]="contactSettings.contactAddress"
              placeholder="Street, City, State ZIP"
            />
          </div>

        </div>

      </div>


      <!-- =====================================================
           FORM DESTINATIONS
           ===================================================== -->

      <div class="settings-group">

        <div class="settings-group-heading">
          <span class="section-kicker">
            Form routing
          </span>

          <h3>
            Email destinations
          </h3>

          <p>
            Choose where website submissions
            should be delivered.
          </p>
        </div>


        <div class="settings-routing-grid">

          <!-- CONTACT -->

          <article class="setting-destination-card">

            <div class="setting-destination-icon">
              ✉
            </div>

            <div>
              <strong>
                Contact Us
              </strong>

              <p>
                General questions and website
                contact requests.
              </p>
            </div>

            <div class="form-field">
              <label for="routing-contact-email">
                Recipient email
              </label>

              <input
                id="routing-contact-email"
                name="routingContactEmail"
                type="email"
                [(ngModel)]="contactSettings.contactEmail"
                required
              />
            </div>

          </article>


          <!-- RESUMES -->

          <article class="setting-destination-card">

            <div class="setting-destination-icon">
              ▤
            </div>

            <div>
              <strong>
                Employment & resumes
              </strong>

              <p>
                Job applications and uploaded
                resume documents.
              </p>
            </div>

            <div class="form-field">
              <label for="resume-email">
                Recipient email
              </label>

              <input
                id="resume-email"
                name="resumeEmail"
                type="email"
                [(ngModel)]="contactSettings.resumeEmail"
                placeholder="careers@example.com"
                required
              />
            </div>

          </article>


          <!-- DONATIONS -->

          <article class="setting-destination-card">

            <div class="setting-destination-icon">
              ♥
            </div>

            <div>
              <strong>
                Donation requests
              </strong>

              <p>
                Community donation and
                sponsorship requests.
              </p>
            </div>

            <div class="form-field">
              <label for="donation-email">
                Recipient email
              </label>

              <input
                id="donation-email"
                name="donationEmail"
                type="email"
                [(ngModel)]="contactSettings.donationEmail"
                placeholder="community@example.com"
                required
              />
            </div>

          </article>

        </div>

      </div>


      <div class="form-actions">

        <span class="saved-detail">
          Changes take effect immediately after saving.
        </span>

        <button
          type="submit"
          class="primary-btn"
          [disabled]="siteSettingsSaving"
        >
          @if (siteSettingsSaving) {
            Saving...
          } @else {
            Save website settings
          }
        </button>

      </div>

    </form>

  }

</div>



<!-- =========================================================
     PASSWORD SETTINGS
     ========================================================= -->

<div class="section-heading settings-password-heading">
  <div>
    <span class="section-kicker danger-text">
      Protected settings
    </span>

    <h2>
      Password management
    </h2>

    <p>
      Changing a shared password affects everyone
      who uses that portal.
    </p>
  </div>
</div>


<section class="danger-zone">

  <div class="danger-header">

    <div class="danger-symbol">
      !
    </div>

    <div>
      <span>
        Danger zone
      </span>

      <h3>
        Shared portal passwords
      </h3>

      <p>
        The existing password is always required.
        Saved passwords are never displayed.
      </p>
    </div>

  </div>


  <article
    class="password-row"
    *ngFor="let role of accountRoles"
  >

    <div class="password-summary">

      <div>
        <strong>
          {{ role }} password
        </strong>

        <span>
          Controls access to the
          {{ role | lowercase }} portal
        </span>
      </div>

      <button
        type="button"
        class="danger-outline-btn"
        (click)="togglePasswordPanel(role)"
      >
        {{
          expandedPasswordRole === role
            ? 'Close'
            : 'View'
        }}
      </button>

    </div>


    <form
      *ngIf="expandedPasswordRole === role"
      class="password-form"
      (ngSubmit)="savePassword(role)"
      novalidate
    >

      <!-- CURRENT PASSWORD -->

      <div class="form-field">
        <label [for]="role + '-current'">
          Current {{ role }} password
        </label>

        <div class="password-input">

          <input
            [id]="role + '-current'"
            [type]="
              isPasswordVisible(
                role,
                'currentPassword'
              )
                ? 'text'
                : 'password'
            "
            [(ngModel)]="passwordForms[role].currentPassword"
            name="currentPassword"
            autocomplete="current-password"
          />

          <button
            type="button"
            (click)="
              togglePasswordVisibility(
                role,
                'currentPassword'
              )
            "
          >
            {{
              isPasswordVisible(
                role,
                'currentPassword'
              )
                ? 'Hide'
                : 'Show'
            }}
          </button>

        </div>
      </div>


      <!-- NEW PASSWORD -->

      <div class="password-grid">

        <div class="form-field">
          <label [for]="role + '-new'">
            New password
          </label>

          <div class="password-input">

            <input
              [id]="role + '-new'"
              [type]="
                isPasswordVisible(
                  role,
                  'newPassword'
                )
                  ? 'text'
                  : 'password'
              "
              [(ngModel)]="passwordForms[role].newPassword"
              name="newPassword"
              autocomplete="new-password"
            />

            <button
              type="button"
              (click)="
                togglePasswordVisibility(
                  role,
                  'newPassword'
                )
              "
            >
              {{
                isPasswordVisible(
                  role,
                  'newPassword'
                )
                  ? 'Hide'
                  : 'Show'
              }}
            </button>

          </div>
        </div>


        <div class="form-field">
          <label [for]="role + '-confirm'">
            Confirm new password
          </label>

          <div class="password-input">

            <input
              [id]="role + '-confirm'"
              [type]="
                isPasswordVisible(
                  role,
                  'confirmPassword'
                )
                  ? 'text'
                  : 'password'
              "
              [(ngModel)]="passwordForms[role].confirmPassword"
              name="confirmPassword"
              autocomplete="new-password"
            />

            <button
              type="button"
              (click)="
                togglePasswordVisibility(
                  role,
                  'confirmPassword'
                )
              "
            >
              {{
                isPasswordVisible(
                  role,
                  'confirmPassword'
                )
                  ? 'Hide'
                  : 'Show'
              }}
            </button>

          </div>
        </div>

      </div>


      <span
        *ngIf="passwordErrors[role]"
        class="field-error"
      >
        {{ passwordErrors[role] }}
      </span>


      <div class="form-actions">

        <button
          type="button"
          class="text-btn"
          (click)="togglePasswordPanel(role)"
        >
          Cancel
        </button>

        <button
          type="submit"
          class="danger-btn"
        >
          Save new {{ role }} password
        </button>

      </div>

    </form>

  </article>

</section>



<!-- =========================================================
     WEBSITE EMAIL DELIVERY
     ========================================================= -->

<div class="section-heading email-settings-heading">
  <div>
    <span class="section-kicker">
      Email delivery
    </span>

    <h2>
      Website sender account
    </h2>

    <p>
      Manage the Gmail account the website uses
      to send form submissions and automated email.
    </p>
  </div>
</div>


<div class="panel settings-panel email-settings-panel">

  @if (emailSettingsLoading) {

    <div class="empty-state large">
      <strong>
        Loading website email settings...
      </strong>
    </div>

  } @else {

    <form
      (ngSubmit)="saveEmailSettings()"
      novalidate
    >

      <!-- =====================================================
           IMPORTANT WARNING
           ===================================================== -->

      <div class="email-settings-warning">

        <div class="email-warning-symbol">
          !
        </div>

        <div>
          <strong>
            Important: this account sends email from the website.
          </strong>

          <p>
            <strong>
              Do not change these settings unless you know
              what you are doing.
            </strong>

            Changing the sender email or App Password incorrectly
            can prevent donation requests, contact forms,
            employment submissions, and other website messages
            from being delivered.
          </p>
        </div>

      </div>


      <!-- =====================================================
           SENDER ACCOUNT
           ===================================================== -->

      <div class="settings-group">

        <div class="settings-group-heading">
          <span class="section-kicker">
            Sender account
          </span>

          <h3>
            Website email credentials
          </h3>

          <p>
            The website authenticates with this Gmail account
            whenever it sends an email.
          </p>
        </div>


        <div class="email-default-account">
          <strong>
            Default Holowicki website account
          </strong>

          <p>
            <strong>
              noreply.holowickimcd@gmail.com
            </strong>
            was created specifically for the Holowicki website
            and transferred with the website during handoff.
            Under normal circumstances, this account should
            not need to be changed.
          </p>
        </div>


        <div class="form-grid">

          <div class="form-field full-width">
            <label for="sender-email">
              Website sender email
            </label>

            <input
              id="sender-email"
              name="senderEmail"
              type="email"
              [(ngModel)]="emailSettings.senderEmail"
              placeholder="noreply.holowickimcd@gmail.com"
              autocomplete="email"
              required
            />

            <small>
              This is the Gmail account messages are sent
              <strong>from</strong>. It is separate from the
              recipient email addresses configured above.
            </small>
          </div>


          <div class="form-field full-width">
            <label for="email-app-password">
              Gmail App Password
            </label>

            <input
              id="email-app-password"
              name="emailAppPassword"
              type="password"
              [(ngModel)]="emailAppPassword"
              placeholder="Enter a new App Password"
              autocomplete="new-password"
            />

            @if (emailSettings.hasAppPassword) {

              <small class="credential-status">
                <strong>
                  App Password configured.
                </strong>

                Leave this field blank to keep the
                existing App Password.
              </small>

            } @else {

              <small class="credential-warning">
                <strong>
                  No App Password is currently configured.
                </strong>

                An App Password must be entered before
                the website can send email.
              </small>

            }

          </div>

        </div>

      </div>


      <!-- =====================================================
           APP PASSWORD HELP
           ===================================================== -->

      <details class="email-help">

        <summary>
          Email setup and App Password help
        </summary>

        <div class="email-help-content">

          <div class="email-help-intro">
            <h3>
              Changing the website sender
            </h3>

            <p>
              These steps are only necessary if the existing
              website Gmail account is replaced, its credentials
              change, or a new App Password must be generated.
            </p>
          </div>


          <ol class="email-help-steps">

            <li>
              <strong>
                Create or sign into the Gmail account.
              </strong>

              <p>
                Use the Gmail account that should be responsible
                for sending email from the website.
              </p>
            </li>

            <li>
              <strong>
                Enable 2-Step Verification.
              </strong>

              <p>
                Open the Google Account security settings and
                make sure 2-Step Verification is enabled.
                Google requires this before an App Password
                can be created.
              </p>
            </li>

            <li>
              <strong>
                Open Google App Passwords.
              </strong>

              <p>
                From the Google Account security settings,
                locate App Passwords after 2-Step Verification
                has been enabled.
              </p>
            </li>

            <li>
              <strong>
                Create an App Password for the website.
              </strong>

              <p>
                Create a new App Password with a recognizable
                name such as
                <strong>Holowicki Website</strong>.
              </p>
            </li>

            <li>
              <strong>
                Copy the generated App Password.
              </strong>

              <p>
                Google only displays the generated credential
                when it is created. Copy it before leaving
                the page.
              </p>
            </li>

            <li>
              <strong>
                Update the settings above.
              </strong>

              <p>
                Enter the Gmail address into
                <strong>Website sender email</strong>
                and paste the generated credential into
                <strong>Gmail App Password</strong>.
              </p>
            </li>

            <li>
              <strong>
                Save email settings.
              </strong>

              <p>
                Saving immediately changes the account and
                credentials used by the website for outgoing email.
              </p>
            </li>

          </ol>


          <div class="email-help-note important">
            <strong>
              Changed the Gmail account password?
            </strong>

            <p>
              If the password for the Gmail account is changed,
              the website's existing App Password may no longer
              work. Generate a new App Password through Google
              and enter it above.
            </p>
          </div>


          <div class="email-help-note">
            <strong>
              Holowicki default configuration
            </strong>

            <p>
              The website was originally configured with
              <strong>
                noreply.holowickimcd@gmail.com
              </strong>.
              That account was created specifically for website
              email delivery and handed off with the website.
              There is normally no reason to replace it.
            </p>
          </div>


          <div class="email-help-note">
            <strong>
              About the saved App Password
            </strong>

            <p>
              The existing App Password cannot be viewed from
              this dashboard. The website stores the credential
              securely and only allows it to be replaced.
            </p>
          </div>

        </div>

      </details>


      <!-- =====================================================
           SAVE
           ===================================================== -->

      <div class="form-actions">

        <span class="saved-detail">
          <strong>
            Saving changes immediately changes the account
            used to send website email.
          </strong>
        </span>

        <button
          type="submit"
          class="primary-btn"
          [disabled]="emailSettingsSaving"
        >
          @if (emailSettingsSaving) {
            Saving...
          } @else {
            Save email settings
          }
        </button>

      </div>

    </form>

  }

</div>
